/** Вход: телефон → код из SMS. */

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';

import { ApiRequestError, api } from '../api/client';
import { CodeInput } from '../components/CodeInput';
import { formatCountdown, formatPhone, normalizePhone } from '../lib/format';

interface LoginScreenProps {
  /** Сервис работает на моках — подскажем тестовый код. */
  mock: boolean;
  onAuthenticated(): void;
}

type Step = 'phone' | 'code';

export function LoginScreen({ mock, onAuthenticated }: LoginScreenProps): JSX.Element {
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [challengeId, setChallengeId] = useState('');
  // Ситидрайв присылает шестизначный код; точную длину всё равно вернёт сервер.
  const [codeLength, setCodeLength] = useState(6);
  const [code, setCode] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const phoneRef = useRef<HTMLInputElement>(null);

  const digits = normalizePhone(phone);
  const phoneValid = digits.length === 11;

  useEffect(() => {
    if (step === 'phone') phoneRef.current?.focus();
  }, [step]);

  // Таймер повторной отправки.
  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = window.setInterval(() => {
      setResendIn((value) => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendIn]);

  const describe = (unknownError: unknown): string => {
    if (unknownError instanceof ApiRequestError) {
      if (unknownError.isNetwork) return 'Нет связи с сервером. Проверьте интернет.';
      return unknownError.message;
    }
    return 'Что-то пошло не так. Попробуйте ещё раз.';
  };

  const requestCode = useCallback(
    async (target: string) => {
      setBusy(true);
      setError(null);
      try {
        const result = await api.loginStart(target);
        setChallengeId(result.challengeId);
        setCodeLength(result.codeLength ?? 6);
        setResendIn(result.resendAfterS ?? 60);
        setCode('');
        setStep('code');
      } catch (caught) {
        setError(describe(caught));
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  const submitPhone = (event: FormEvent): void => {
    event.preventDefault();
    if (!phoneValid || busy) return;
    void requestCode(digits);
  };

  const submitCode = useCallback(
    async (value: string) => {
      if (busy) return;
      setBusy(true);
      setError(null);
      try {
        await api.loginConfirm({ challengeId, code: value, phone: digits });
        onAuthenticated();
      } catch (caught) {
        setError(describe(caught));
        setCode('');
      } finally {
        setBusy(false);
      }
    },
    [busy, challengeId, digits, onAuthenticated],
  );

  return (
    <main className="login">
      <div className="login__top">
        <div className="login__logo" aria-hidden="true">
          ⛽
        </div>
        <h1 className="login__title">Заправщик</h1>
        <p className="login__sub">
          Находим рядом машины каршеринга с пустым баком и строим маршрут заправок.
          {' '}
          <b>1000 ₽</b> за каждую от 20 литров.
        </p>
      </div>

      {step === 'phone' ? (
        <form className="login__form" onSubmit={submitPhone} noValidate>
          <label className="field">
            <span className="field__label">Номер телефона</span>
            <input
              ref={phoneRef}
              className="field__input field__input--lg num"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              name="phone"
              placeholder="+7 (___) ___-__-__"
              value={formatPhone(phone)}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? 'login-error' : undefined}
              onChange={(event) => setPhone(event.target.value)}
              disabled={busy}
            />
            <span className="field__hint">Тот же номер, что и в Ситидрайве</span>
          </label>

          {error ? (
            <p className="field__error" id="login-error" role="alert">
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            className="btn btn--primary btn--block btn--lg"
            disabled={!phoneValid || busy}
          >
            {busy ? 'Отправляем код…' : 'Получить код'}
          </button>
        </form>
      ) : (
        <form
          className="login__form"
          onSubmit={(event) => {
            event.preventDefault();
            void submitCode(code);
          }}
          noValidate
        >
          <p className="login__sent">
            Код отправлен на <b className="num">{formatPhone(digits)}</b>
          </p>

          <CodeInput
            length={codeLength}
            value={code}
            disabled={busy}
            invalid={Boolean(error)}
            onChange={setCode}
            onComplete={(value) => void submitCode(value)}
          />

          {mock ? (
            <p className="login__mock" role="note">
              Тестовый режим: подойдёт код <b className="num">000000</b>
            </p>
          ) : null}

          {error ? (
            <p className="field__error" role="alert">
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            className="btn btn--primary btn--block btn--lg"
            disabled={code.length < codeLength || busy}
          >
            {busy ? 'Проверяем…' : 'Войти'}
          </button>

          <div className="login__row">
            <button
              type="button"
              className="btn btn--link"
              onClick={() => {
                setStep('phone');
                setError(null);
              }}
            >
              Изменить номер
            </button>
            <button
              type="button"
              className="btn btn--link"
              disabled={resendIn > 0 || busy}
              onClick={() => void requestCode(digits)}
            >
              {resendIn > 0
                ? `Повторить через ${formatCountdown(resendIn)}`
                : 'Отправить код ещё раз'}
            </button>
          </div>
        </form>
      )}

      <p className="login__legal">
        Мы не храним пароль от Ситидрайва — вход по коду из SMS.
      </p>
    </main>
  );
}
